import { Fragment, useEffect, useState, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, ChevronDoubleLeftIcon } from '@heroicons/react/20/solid';
import { comboBoxStyles } from './ComboBox'; 
import { cx } from 'class-variance-authority';

export type ComboBoxOption = {
  value: string;
  label: string;
  id: string;
};

type Props = {
  options: ComboBoxOption[];
  value: string[];
  placeholder?: string;
  title?: string;
  showAllOptions?: boolean;
  allOptionLabel?: string;
  allOptionIcon?: 'checkbox' | 'chevron';
  isDisabled?: boolean;
  isShowMore?: boolean;
  width?: string;
  dropdownBoxSize?: string;
  onChange: (options: ComboBoxOption[]) => void;
  onShowMore?: () => void;
};

export default function MultiSelectListBox({
  options,
  value,
  placeholder = 'Select...',
  title,
  showAllOptions = true,
  allOptionLabel,
  allOptionIcon = 'checkbox',
  width = '100%',
  dropdownBoxSize = '200px',
  isShowMore = false,
  isDisabled = false,
  onChange,
  onShowMore
}: Props) {
  const [allSelection, setAllSection] = useState<boolean>(false);

  const selectedOptions = useMemo(() => {
    // Defensive check: ensure value and options are arrays
    const safeValue = Array.isArray(value) ? value : [];
    const safeOptions = Array.isArray(options) ? options : [];

    const hasAll = safeValue.length === 0 || safeValue.includes('__all__');
    setAllSection(hasAll);

    if (hasAll) {
      return [];
    }

    const sanitized = safeValue.filter((v) => typeof v === 'string' && v !== '__all__');
    return safeOptions.filter((o) => sanitized.includes(o.value));
  }, [value, options]);

  const displayText =
    selectedOptions.length === 0 || selectedOptions[0]?.value === '__all__'
      ? placeholder
      : selectedOptions.map((o) => o.label).join(', ');

  const truncated = displayText.length > 18 ? `${displayText.slice(0, 18).trim()}…` : displayText;

  return (
    <div className="relative" style={{ width: width }}>
      <Listbox
        value={selectedOptions}
        onChange={(newSelected) => {
          const selectedAll = newSelected.some((o) => o.value === '__all__');
          if (selectedAll) {
            setAllSection(true);
            onChange([]);
            return;
          }

          const cleaned = newSelected
            .filter(
              (o): o is ComboBoxOption => !!o && typeof o.value === 'string' && o.value !== '__all__'
            )
            .map((o) => ({ ...o }));

          setAllSection(false);
          onChange(cleaned);
        }}

        multiple
        disabled={isDisabled}
      >

        <div className="relative">
          <Listbox.Button
            className={cx(
              comboBoxStyles({ disabled: isDisabled }),
              'pr-[4rem] truncate overflow-hidden text-ellipsis text-left w-full'
            )}
          >
            {truncated || placeholder}
            <span className="absolute inset-y-0 right-[0.8rem] md:right-[2rem] flex items-center pr-2 pointer-events-none">
              <ChevronDownIcon className="h-6 w-6 text-brand-gray" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              className="
                absolute z-overlay mt-2 min-w-[20rem] rounded-[.8rem]
                bg-brand-white shadow-lg ring-1 ring-brand-black 
                 focus:outline-hidden p-4
                max-h-[400px]
              "
              style={{ width: dropdownBoxSize }}
            >
              {(showAllOptions || (!showAllOptions && value.length > 0)) && allOptionLabel && (
                <Listbox.Option
                  key={"__all__"}
                  value={{ id: '__all__', value: '__all__', label: '__all__' }}
                  className={() =>
                    cx(`relative cursor-pointer select-none py-2 flex items-center ${allOptionIcon === "checkbox" ? 'px-4' : 'px-0'}`,)
                  }
                >
                  {allOptionIcon === 'checkbox' ? (
                    <input
                      type="checkbox"
                      checked={allSelection}
                      readOnly
                      className="mr-3 h-6 w-6 text-brand-secondary cursor-pointer"
                    />
                  ) : allOptionIcon === "chevron" ? (
                    <ChevronDoubleLeftIcon className = "h-6 w-6" />
                  ) : (
                    <></>
                  )}
                  
                  <span className="truncate text-[14px]">{allOptionLabel}</span>
                </Listbox.Option>
              )}

              {title && (
                <div className="font-medium text-[17px] pb-[1rem]">
                  {title}
                </div>
              )}
              
              <div className="max-h-[300px] overflow-y-scroll">
                {options.map((option) => {
                  const selected = selectedOptions.some((o) => o.value === option.value);
                  return (
                    <Listbox.Option
                      key={option.id}
                      value={option}
                      className={({ active }) =>
                        cx(
                          'relative cursor-pointer select-none px-4 py-2 flex items-center',
                          active ? 'bg-gray-100' : '',
                          selected ? 'font-medium' : 'font-normal'
                        )
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        readOnly
                        className="mr-3 h-6 w-6 text-brand-secondary cursor-pointer"
                      />
                      <span className="truncate text-[14px]">{option.label}</span>
                      {selected && (
                        <span className="absolute inset-y-0 left-2 flex items-center text-brand-secondary">
                          <CheckIcon className="h-6 w-6" aria-hidden="true" />
                        </span>
                      )}
                    </Listbox.Option>
                  );
                })}
              </div>

              {onShowMore && (
                <div
                  className='relative cursor-pointer select-none px-4 py-2 flex items-center'
                  onClick={onShowMore}
                >
                  <span className="truncate text-[14px]">{isShowMore  ? "- Show less" : "+ Show more"}</span>
                </div>
              )}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
}
